import type { CheckTarget } from "./check-targets.ts";
import type { AcceptanceCriterion, Deliverable, Priority, RiskFlag, Size, TaskKind } from "./types.ts";

/** The shortest interval a schedule may fire at (docs/specs/mission-control.md §7 MC3 AC1). */
export const MIN_SCHEDULE_INTERVAL_MINUTES = 5;
/** The longest: 30 days. Anything rarer is not a recurring check. */
export const MAX_SCHEDULE_INTERVAL_MINUTES = 30 * 24 * 60;

const MINUTE_MS = 60_000;
const DAY_MS = 24 * 60 * MINUTE_MS;
// IANA offsets range from UTC-12 to UTC+14. Two extra hours also cover ordinary DST jumps.
const LOCAL_INSTANT_SEARCH_RADIUS_MS = 16 * 60 * MINUTE_MS;

type CalendarDate = { year: number; month: number; day: number };
type ZonedMinute = CalendarDate & { hour: number; minute: number };

export type NextDueAtInput = { timeOfDay: string; timezone: string; after: number };
export type RoutineWindow = { windowStart: number; windowEnd: number };
export type ScheduledRoutineWindowInput = Pick<NextDueAtInput, "timeOfDay" | "timezone"> & { scheduledAt: number };
export type ManualRoutineWindowInput = { now: number };
export type RoutineWindowInput =
  | ({ trigger: "schedule" } & ScheduledRoutineWindowInput)
  | ({ trigger: "manual" } & ManualRoutineWindowInput);

function zonedFormatter(timezone: string): Intl.DateTimeFormat {
  return new Intl.DateTimeFormat("en-US-u-ca-gregory-nu-latn", {
    timeZone: timezone,
    calendar: "gregory",
    numberingSystem: "latn",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  });
}

function zonedMinute(formatter: Intl.DateTimeFormat, instant: number): ZonedMinute {
  const values: Partial<Record<Intl.DateTimeFormatPartTypes, number>> = {};
  for (const part of formatter.formatToParts(instant)) {
    if (part.type === "year" || part.type === "month" || part.type === "day" || part.type === "hour" || part.type === "minute") {
      values[part.type] = Number(part.value);
    }
  }
  return {
    year: values.year!,
    month: values.month!,
    day: values.day!,
    hour: values.hour!,
    minute: values.minute!,
  };
}

function parseTimeOfDay(timeOfDay: string): { hour: number; minute: number } {
  const match = /^(?:[01]\d|2[0-3]):[0-5]\d$/.exec(timeOfDay);
  if (!match) throw new RangeError("timeOfDay must be HH:MM in 24-hour time");
  return { hour: Number(timeOfDay.slice(0, 2)), minute: Number(timeOfDay.slice(3, 5)) };
}

function sameDate(a: CalendarDate, b: CalendarDate): boolean {
  return a.year === b.year && a.month === b.month && a.day === b.day;
}

function addCalendarDays(date: CalendarDate, days: number): CalendarDate {
  const shifted = new Date(Date.UTC(date.year, date.month - 1, date.day + days));
  return { year: shifted.getUTCFullYear(), month: shifted.getUTCMonth() + 1, day: shifted.getUTCDate() };
}

/**
 * Resolve one local calendar date to its scheduled UTC instant. In an overlap the scan encounters
 * the first occurrence first. In a gap there is no exact match, so it returns the first real wall
 * minute after the requested time on that date.
 */
function scheduledInstantOnDate(date: CalendarDate, timeOfDay: string, formatter: Intl.DateTimeFormat): number | null {
  const time = parseTimeOfDay(timeOfDay);
  const wallAsUtc = Date.UTC(date.year, date.month - 1, date.day, time.hour, time.minute);
  const start = wallAsUtc - LOCAL_INSTANT_SEARCH_RADIUS_MS;
  const end = wallAsUtc + LOCAL_INSTANT_SEARCH_RADIUS_MS;
  let firstAfterGap: number | null = null;

  for (let instant = start; instant <= end; instant += MINUTE_MS) {
    const local = zonedMinute(formatter, instant);
    if (!sameDate(local, date)) continue;
    if (local.hour === time.hour && local.minute === time.minute) return instant;
    if (firstAfterGap === null && local.hour * 60 + local.minute > time.hour * 60 + time.minute) firstAfterGap = instant;
  }
  return firstAfterGap;
}

function assertFiniteInstant(value: number, name: string): void {
  if (!Number.isFinite(value)) throw new RangeError(`${name} must be a finite epoch millisecond value`);
}

/**
 * The first daily wall-clock occurrence strictly after `after` (routines §4.2). Calendar dates are
 * advanced in `timezone`; elapsed 24-hour increments are never used.
 */
export function nextDueAt({ timeOfDay, timezone, after }: NextDueAtInput): number {
  assertFiniteInstant(after, "after");
  const formatter = zonedFormatter(timezone);
  let date: CalendarDate = zonedMinute(formatter, after);

  // A whole local date may theoretically be skipped by a political offset change. Keep advancing
  // by calendar date so that such a date does not turn into a duplicate occurrence.
  for (;;) {
    const instant = scheduledInstantOnDate(date, timeOfDay, formatter);
    if (instant !== null && instant > after) return instant;
    date = addCalendarDays(date, 1);
  }
}

/** A scheduled run covers the interval from the previous zoned occurrence to this scheduled instant. */
export function scheduledRoutineWindow({ timeOfDay, timezone, scheduledAt }: ScheduledRoutineWindowInput): RoutineWindow {
  assertFiniteInstant(scheduledAt, "scheduledAt");
  const formatter = zonedFormatter(timezone);
  let date = addCalendarDays(zonedMinute(formatter, scheduledAt), -1);
  for (;;) {
    const windowStart = scheduledInstantOnDate(date, timeOfDay, formatter);
    if (windowStart !== null && windowStart < scheduledAt) return { windowStart, windowEnd: scheduledAt };
    date = addCalendarDays(date, -1);
  }
}

/** A manual run always covers the 24 elapsed hours ending at its injected clock value. */
export function manualRoutineWindow({ now }: ManualRoutineWindowInput): RoutineWindow {
  assertFiniteInstant(now, "now");
  return { windowStart: now - DAY_MS, windowEnd: now };
}

/** Select the scheduled or manual window rule without consulting the system clock. */
export function routineWindow(input: RoutineWindowInput): RoutineWindow {
  return input.trigger === "schedule" ? scheduledRoutineWindow(input) : manualRoutineWindow(input);
}

/**
 * One target of a schedule (MC3 AC4): what a created task probes, plus the hub-owned state of its
 * current outage. `subjectDecisionId` is the open (or last answered) subject decision for this
 * target and `coveredUntil` the end of its answer's coverage window, epoch ms. Both are null until
 * MC3c populates them, and neither is ever copied into a task.
 */
export type ScheduleTarget = CheckTarget & {
  subjectDecisionId: string | null;
  coveredUntil: number | null;
  /** `file_bug` only: the bug task whose completion ends the coverage window early. */
  bugTaskId?: string | null;
  /** When the subject decision last became open (created or reopened), epoch ms. */
  openSince?: number | null;
  /** When the decision's 24-hour open note was written on this target (AC5); once per open spell. */
  longOpenNotedAt?: number | null;
};

/** The hub-owned option ids of a subject decision (MC3 AC4), and the coverage window each sets. */
export const SUBJECT_OPTION_IDS = ["investigating", "snooze_24h", "file_bug"] as const;
export type SubjectOptionId = (typeof SUBJECT_OPTION_IDS)[number];
export const SUBJECT_WINDOW_MS: Record<SubjectOptionId, number> = {
  investigating: 4 * 60 * 60_000,
  snooze_24h: 24 * 60 * 60_000,
  file_bug: 24 * 60 * 60_000,
};
/** The hub-owned option ids of a routine proposal (routines §6.4), chosen by the subject's kind. */
export const ROUTINE_PROPOSAL_OPTION_IDS = ["create_task", "dismiss", "snooze_7d"] as const;
export type RoutineProposalOptionId = (typeof ROUTINE_PROPOSAL_OPTION_IDS)[number];
export const ROUTINE_PROPOSAL_MAX_PER_RUN = 3;
export const ROUTINE_PROPOSAL_MAX_REASON = 300;
export const ROUTINE_PROPOSAL_SNOOZE_MS = 7 * 24 * 60 * 60_000;
/** A subject decision open longer than this is noted on its schedule (MC3 AC5). */
export const SUBJECT_OPEN_NOTE_MS = 24 * 60 * 60_000;

/** True while an answered subject decision still covers its target: inside the window and, for `file_bug`, the bug not yet done. */
export function subjectCoverageActive(target: Pick<ScheduleTarget, "coveredUntil">, bugEnded: boolean, now: number): boolean {
  return target.coveredUntil !== null && now < target.coveredUntil && !bugEnded;
}

/** The task each firing creates: a task's own fields, minus what the schedule owns (targets) or cannot repeat (dependencies, intent). */
export type ScheduleTemplate = {
  title: string;
  description?: string;
  kind?: TaskKind;
  deliverable?: Deliverable;
  acceptanceCriteria?: (AcceptanceCriterion | string)[];
  areas?: string[];
  labels?: string[];
  skills?: string[];
  size?: Size;
  flags?: RiskFlag[];
  priority?: Priority;
};

export type ScheduleDTO = {
  id: string;
  projectId: string;
  agentId: string;
  intervalMinutes: number;
  template: ScheduleTemplate;
  targets: ScheduleTarget[];
  enabled: boolean;
  lastFiredAt: number | null;
  nextDueAt: number;
  createdBy: string;
  createdAt: number;
  updatedAt: number;
};

/** A target as stored on a schedule: the probe definition, with no outage yet. */
export function newScheduleTarget(t: CheckTarget): ScheduleTarget {
  return { ...checkTargetOf(t), subjectDecisionId: null, coveredUntil: null };
}

/** The probe definition alone, as a fresh object in the MC1 `CheckTarget` shape (no subject state). */
export function checkTargetOf(t: CheckTarget): CheckTarget {
  return { url: t.url, expectStatus: t.expectStatus, maxMs: t.maxMs, ...(t.mustContain !== undefined ? { mustContain: t.mustContain } : {}) };
}

/**
 * The task a schedule creates (MC3 AC1): its template, with a copy of the schedule's targets as
 * `checkTargets` (MC1 AC8). The caller adds the project and sets `schedule_id`; neither comes from the template.
 */
export function taskFromSchedule(schedule: Pick<ScheduleDTO, "template" | "targets">): ScheduleTemplate & { checkTargets: CheckTarget[] } {
  const t = schedule.template;
  return {
    ...t,
    acceptanceCriteria: t.acceptanceCriteria ? [...t.acceptanceCriteria] : undefined,
    areas: t.areas ? [...t.areas] : undefined,
    labels: t.labels ? [...t.labels] : undefined,
    skills: t.skills ? [...t.skills] : undefined,
    flags: t.flags ? [...t.flags] : undefined,
    checkTargets: schedule.targets.map(checkTargetOf),
  };
}

/** Readable problems with a schedule's interval and targets that no task check would catch. */
export function scheduleProblems(input: { intervalMinutes: number; targets: readonly CheckTarget[]; template: Pick<ScheduleTemplate, "deliverable" | "title"> }): string[] {
  const problems: string[] = [];
  if (!Number.isInteger(input.intervalMinutes) || input.intervalMinutes < MIN_SCHEDULE_INTERVAL_MINUTES || input.intervalMinutes > MAX_SCHEDULE_INTERVAL_MINUTES) {
    problems.push(`intervalMinutes must be a whole number from ${MIN_SCHEDULE_INTERVAL_MINUTES} to ${MAX_SCHEDULE_INTERVAL_MINUTES}`);
  }
  if (!input.template.title.trim()) problems.push("template.title is required");
  const seen = new Set<string>();
  for (const t of input.targets) {
    // A subject target is named by its URL (MC3 AC4), so two targets with one URL would be ambiguous.
    if (seen.has(t.url)) problems.push(`target ${t.url} is listed twice`);
    seen.add(t.url);
  }
  if (input.template.deliverable === "check" && input.targets.length === 0) problems.push("a check schedule needs at least one target");
  return problems;
}
